/* eslint-disable @typescript-eslint/no-explicit-any */
import './queue-grid.scss';

import { SortOrder } from '@converge/data';
import {
  ISortProps,
  ITableColumn,
  PaginatedTable,
} from '../../presentation/components/paginated-table';
import {
  SubmissionFilterInput,
  SubmissionListApplicationData,
  SubmissionListData,
  SubmissionSortKey,
  getPaginatedNodes,
  useSubmissionsQuery,
  useSubmissionsSubscriptionQuery,
} from '@converge/graphql';
import { useEffect, useMemo, useState } from 'react';

import { Box } from '@mui/material';
import { CheckedState } from '../../presentation/components/table-filter';
import { CircularLoader } from '../../presentation/components/circular-loader';

import { ErrorPageLayout } from '../../presentation/layouts';
import { NewConvergeButton } from '../../presentation/components/new-button';
import { PaperLayout } from '../../presentation/components/paper-layout';
import SubmissionFilter from './submission-filter';
import { useConvergeNavigation } from '../../contexts/use-converge-navigation';
import { useTranslation } from 'react-i18next';

interface IQueueGridProps {
  title: string;
  columns: ITableColumn<SubmissionListData, void>[];
  childRowColumns?: ITableColumn<
    SubmissionListApplicationData,
    SubmissionListData
  >[];
  defaultSort: ISortProps;
}

const defaultFilters: CheckedState = {
  broker: [],
  brokerage: [],
  tenant: [],
  dueDate: [],
  policyStartDate: [],
  partner: [],
  type: [],
  program: [],
  receivedAt: [],
  renewalType: [],
  status: [],
  submissionType: [],
  underwriter: [],
  underwriterAssistant: [],
};

const loadFromStorage = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

const buildSubmissionFilters = (
  filters: CheckedState
): SubmissionFilterInput => {
  const nestedFilters: SubmissionFilterInput['nestedFilters'] = {};

  if (filters['brokerage']?.length)
    nestedFilters.agencies = filters['brokerage'];
  if (filters['broker']?.length) nestedFilters.agents = filters['broker'];
  if (filters['underwriter']?.length)
    nestedFilters.underwriters = filters['underwriter'];
  if (filters['underwriterAssistant']?.length)
    nestedFilters.underwriterAssistants = filters['underwriterAssistant'];
  if (filters['program']?.length) nestedFilters.programs = filters['program'];
  if (filters['partner']?.length) nestedFilters.partners = filters['partner'];

  const result: SubmissionFilterInput = {};

  if (Object.keys(nestedFilters).length) result.nestedFilters = nestedFilters;
  if (filters['status']?.length) result.statuses = filters['status'] as any;
  if (filters['renewalType']?.length)
    result.renewalTypes = filters['renewalType'] as any;
  if (filters['type']?.length) result.types = filters['type'] as any;
  if (filters['tenant']?.length) result.tenants = filters['tenant'] as any;
  if (filters['submissionType']?.length === 1)
    result.renewal = filters['submissionType'][0] === 'renewal';

  if (filters['dueDate']?.length) {
    try {
      const { start, end } = JSON.parse(filters['dueDate'][0]);
      result.dueDate = { start, end } as any;
    } catch {
      /* ignore */
    }
  }
  if (filters['receivedAt']?.length) {
    try {
      const { start, end } = JSON.parse(filters['receivedAt'][0]);
      result.receivedAt = { start, end } as any;
    } catch {
      /* ignore */
    }
  }
  if (filters['policyStartDate']?.length) {
    try {
      const { start, end } = JSON.parse(filters['policyStartDate'][0]);
      result.policyStartDate = { start, end } as any;
    } catch {
      /* ignore */
    }
  }

  return result;
};

const QueueGrid = ({
  title,
  columns,
  childRowColumns,
  defaultSort,
}: IQueueGridProps) => {
  const currentRowPageInLocalStorage = localStorage.getItem('currentRowPage');
  const currentSortInLocalStorage = localStorage.getItem('currentSortInPage');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    currentRowPageInLocalStorage ? +currentRowPageInLocalStorage : 10
  );

  const [currentFilters, setCurrentFilters] = useState<CheckedState>(() =>
    loadFromStorage('submissionFilters', defaultFilters)
  );
  const [currentFilterLabels, setCurrentFilterLabels] = useState<CheckedState>(
    () => loadFromStorage('submissionFilterLabels', defaultFilters)
  );
  const [filterLabels, setFilterLabels] =
    useState<CheckedState>(currentFilterLabels);

  const [submissionSort, setSubmissionSort] = useState<ISortProps>(
    currentSortInLocalStorage
      ? JSON.parse(currentSortInLocalStorage)
      : defaultSort
  );
  const { t } = useTranslation();
  const navigate = useConvergeNavigation();

  const submissionFilters = useMemo(
    () => buildSubmissionFilters(currentFilters),
    [currentFilters]
  );

  const {
    data: submissionsData,
    loading,
    error,
    refetch,
  } = useSubmissionsQuery({
    pageNumber: currentPage,
    pageSize: rowsPerPage,
    sort: {
      key: submissionSort.id as SubmissionSortKey,
      order: submissionSort.order,
    },
    filters: submissionFilters,
  });

  const { submissionsStatus, submissionsEventNotice } =
    useSubmissionsSubscriptionQuery();
  useEffect(() => {
    if (submissionsStatus === 'UPDATED' || submissionsEventNotice === true) {
      refetch();
    }
  }, [submissionsStatus, refetch, submissionsEventNotice]);

  if (error) {
    return (
      <ErrorPageLayout
        message={error.message}
        messageId="submissions.list.error"
      />
    );
  }
  const showPrimaryLoadingState =
    loading &&
    (!submissionsData || submissionsData?.submissions.edges.length === 0);

  return (
    <>
      {showPrimaryLoadingState ? (
        <CircularLoader className="circular-loader-style" />
      ) : (
        <PaperLayout className="queue-grid-layout">
          <Box className="table-wrapper">
            <SubmissionFilter
              currentFilters={currentFilters}
              setCurrentFilters={(filters) => {
                setCurrentFilters(filters);
                setCurrentPage(0);
              }}
              currentFilterLabels={currentFilterLabels}
              setCurrentFilterLabels={setCurrentFilterLabels}
              filterLabels={filterLabels}
              setFilterLabels={setFilterLabels}
              setSubmissionFilters={(filters) => {
                setCurrentFilters(filters);
                setCurrentPage(0);
              }}
              resetPageNumber={() => setCurrentPage(0)}
            />
            <PaginatedTable
              columns={columns}
              customSort
              rows={submissionsData?.submissions.edges.map((edge) => edge.node)}
              emptyRowMessage={t(
                'submission.grid.emptyMessage',
                'No submissions found'
              )}
              expandable
              getChildRows={(row) => getPaginatedNodes(row.applications)}
              childColumns={childRowColumns}
              enablePagination
              rowsPerPage={rowsPerPage}
              currentPage={currentPage}
              totalRows={
                submissionsData?.submissions.pageInfo.totalResults || 0
              }
              handleChangeRowsPerPage={(newRowsPerPage) => {
                setRowsPerPage(newRowsPerPage);
                localStorage.setItem(
                  'currentRowPage',
                  JSON.stringify(newRowsPerPage)
                );
                setCurrentPage(0);
              }}
              handleChangePage={setCurrentPage}
              currentSort={submissionSort}
              handleSort={(sort) => {
                if (
                  submissionSort.order === SortOrder.Desc &&
                  submissionSort.id === sort.id
                ) {
                  setSubmissionSort(defaultSort);
                  localStorage.setItem(
                    'currentSortInPage',
                    JSON.stringify(defaultSort)
                  );
                } else {
                  setSubmissionSort(sort);
                  localStorage.setItem(
                    'currentSortInPage',
                    JSON.stringify(sort)
                  );
                }
                setCurrentPage(0);
              }}
            />
          </Box>
          <Box className="create-submission-button-wrapper">
            <div style={{ width: '200px' }}>
              <NewConvergeButton
                onClick={() => {
                  navigate.underwritingCreateSubmission();
                }}
                data-cy="create-submission-button"
                label={'Create Submission'}
              />
            </div>
          </Box>
        </PaperLayout>
      )}
    </>
  );
};

export default QueueGrid;
